import { useMemo } from 'react';

import { useGetPatientCaseWithContentQuery } from '@/@generated/codegen/API';
import { CaseConfig } from '@/modules/case/types';

/**
 * Hook to fetch case details (content) from GraphQL.
 *
 * Previously this fetched from S3 via configUrl. Now it uses the GraphQL
 * getPatientCaseWithContent query and parses the stringifiedContent field.
 *
 * @param caseId - The GraphQL ID of the patient case (or null to skip)
 * @returns Case details including slides, displayName, etc.
 */
export const useCaseDetails = (caseId: string | null) => {
  const { data, loading, error } = useGetPatientCaseWithContentQuery({
    variables: {
      input: { patientCaseId: caseId! },
    },
    skip: !caseId,
    fetchPolicy: 'network-only', // Always fetch fresh content
  });

  const details = useMemo<CaseConfig | null>(() => {
    if (!data?.getPatientCase?.stringifiedContent) return null;

    try {
      const caseConfig = JSON.parse(data.getPatientCase.stringifiedContent) as CaseConfig;

      // Validate required fields exist
      const anamneseSlide = caseConfig.slides?.find((slide) => slide.type === 'anamnese');
      if (!anamneseSlide) {
        console.error('No anamnese slide found in case configuration');
        return null;
      }

      const anamneseItem = anamneseSlide.items?.find((item) => item.type === 'anamnese');
      if (!anamneseItem) {
        console.error('No anamnese item found in anamnese slide');
        return null;
      }

      return caseConfig;
    } catch (err) {
      console.error('Error parsing case content:', err);
      return null;
    }
  }, [data]);

  return {
    details,
    loading,
    error: error?.message || null,
  };
};
